<template>
    <div class="main">
        <div id="Nav" class="left">
            <div class="back"><button @click="goBack()" class="button icon fastic-circle-left" :aria-label="localizer.get('App.Common.PageBack')"></button></div>
            <div class="content">
                <h1 class="maintitle s60">{{ localizer.get("Account.Edit.EditProfileTitle") }}</h1>
                <div class="desc">{{ localizer.get("Account.Edit.EditProfileDesc") }}</div>
                <div class="buttons">
                    <div class="cta secondary s16" data-color="white"><a :href="routes.MyBeghelliProfile_AccountEdit" class="button">{{ localizer.get("Account.Common.AccountSecurity") }}</a></div>
                    <div class="cta secondary s16" data-color="white"><a :href="routes.Account_Profile_Edit" class="button selected">{{ localizer.get("Account.Common.PersonalData") }}</a></div>
                </div>
            </div>
            <app-footer></app-footer>
        </div>
        <main id="Main" class="right">
            <div class="logo" data-color="cyan50">
                <a :href="routes.Home"><img src="/images/logo.svg" width="125" height="35" :alt="localizer.get('App.Common.Beghelli')" /></a>
            </div>
            <div v-if="editData" @keydown.enter.prevent.stop="submitForm()" class="content">
                <div class="intro">
                    <h2 class="maintitle s36">{{ localizer.get("Account.Edit.UpdateDataProfile") }}</h2>
                </div>
                <div ref="form" class="form">
                    <fieldset>
                        <div v-if="!isCompany" class="group">
                            <div class="item">
                                <label for="Name">{{ localizer.get("App.Form.NameSurname") }}</label>
                                <input type="text" :aria-label="localizer.get('App.Form.Name')" id="Name" :placeholder="localizer.get('App.Form.NamePlaceholder')" v-model.lazy="formData.name" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Name', validationErrors) }, 'input w300']">
                            </div>
                            <div class="item">
                                <input type="text" :aria-label="localizer.get('App.Form.Surname')" id="Surname" :placeholder="localizer.get('App.Form.SurnamePlaceholder')" v-model.lazy="formData.surname" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Surname', validationErrors) }, 'input w300']">
                            </div>
                        </div>
                        <div v-else class="item">
                            <label for="Name">{{ localizer.get("Account.User.Company") }}</label>
                            <input type="text" id="Name" v-model.lazy="formData.name" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Name', validationErrors) }, 'input w300']" />
                        </div>
                    </fieldset>
                    <fieldset>
                        <div class="group">
                            <div v-for="(profileTaxType, index) in profileTaxTypes" :key="index" class="radio">
                                <input type="radio" name="profileTaxType" @change="onProfileTaxTypeChanged()" :id="profileTaxType.value" :value="profileTaxType.value" v-model="formData.profileTaxType">
                                <label :for="profileTaxType.value">{{ profileTaxType.title }}</label>
                            </div>
                        </div>
                    </fieldset>
                    <fieldset>
                        <div class="item" data-label="top">
                            <label for="ProfileSegment">{{ localizer.get("App.Common.AddFunctionalities") }}</label>
                            <select id="ProfileSegment" v-model="formData.profileSegment" :class="[{ 'input-validation-error': formHelper.isControlValidationError('ProfileSegment', validationErrors) }, 'input w460']">
                                <option :value="null">{{ localizer.get("App.Form.Select") }}</option>
                                <option v-for="(profileSegment, index) in profileSegments" :value="profileSegment.value" :key="index">{{ profileSegment.title }}</option>
                            </select>
                        </div>
                    </fieldset>
                    <fieldset>
                        <div class="item">
                            <label for="Country">{{ localizer.get("Account.User.Country") }}</label>
                            <select id="Country" v-model="formData.country" @change="onCountryChanged()" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Country', validationErrors) }, 'input w300']">
                                <option :value="null">{{ localizer.get("App.Form.Select") }}</option>
                                <option v-for="(country, index) in countries" :value="country.value" :key="index">{{ country.title }}</option>
                            </select>
                        </div>
                        <div class="group">
                            <div class="item">
                                <label for="Address">{{ localizer.get("App.Form.AddressLocation") }}</label>
                                <input type="text" :aria-label="localizer.get('App.Form.Address')" id="Address" v-model.lazy="formData.address" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Address', validationErrors) }, 'input w300']" />
                            </div>
                            <div class="item">
                                <input type="text" :aria-label="localizer.get('App.Form.Location')" id="City" v-model.lazy="formData.city" :class="[{ 'input-validation-error': formHelper.isControlValidationError('City', validationErrors) }, 'input w300']" />
                            </div>
                        </div>
                        <div class="item">
                            <label for="TaxCode">{{ localizer.get("App.Common.TaxCode") }}</label>
                            <input type="text" id="TaxCode" v-model.lazy="formData.taxCode" :class="[{ 'input-validation-error': formHelper.isControlValidationError('TaxCode', validationErrors) }, 'input w300']" />
                        </div>
                        <div class="group">
                            <div class="item">
                                <label for="PostCode">{{ localizer.get("App.Form.PostCodeProvince") }}</label>
                                <input type="text" :aria-label="localizer.get('App.Form.PostCode')" id="PostCode" v-model.lazy="formData.postCode" :class="[{ 'input-validation-error': formHelper.isControlValidationError('PostCode', validationErrors) }, 'input w300']" />
                            </div>
                            <div class="item">
                                <select id="Province" :aria-label="localizer.get('App.Form.Province')" v-model="formData.province" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Province', validationErrors) }, 'input w300']">
                                    <option :value="null">{{ localizer.get("App.Form.Select") }}</option>
                                    <option v-for="(province, index) in provinces" :value="province.value" :key="index">{{ province.title }}</option>
                                </select>
                            </div>
                        </div>
                        <template v-if="isCompany">
                            <div class="group">
                                <div class="item">
                                    <label for="IVACode">{{ localizer.get("App.Form.VATCodeActivity") }}</label>
                                    <input type="text" :aria-label="localizer.get('App.Form.VATCode')" id="IVACode" v-model.lazy="formData.ivaCode" :class="[{ 'input-validation-error': formHelper.isControlValidationError('IVACode', validationErrors) }, 'input w300']" />
                                </div>
                                <div class="item">
                                    <select id="ActivityType" :aria-label="localizer.get('App.Form.Activity')" v-model="formData.activityType" :class="[{ 'input-validation-error': formHelper.isControlValidationError('ActivityType', validationErrors) }, 'input w300']">
                                        <option :value="null">{{ localizer.get("App.Form.Select") }}</option>
                                        <option v-for="(activityType, index) in activityTypes" :value="activityType.value" :key="index">{{ activityType.title }}</option>
                                    </select>
                                </div>
                            </div>
                            <div class="group">
                                <div class="item">
                                    <label for="SDICode">{{ localizer.get("App.Form.SDICode_PEC") }}</label>
                                    <input type="text" :aria-label="localizer.get('App.Form.SDICode')" id="SDICode" v-model.lazy="formData.sdiCode" :class="[{ 'input-validation-error': formHelper.isControlValidationError('SDICode', validationErrors) }, 'input w300']" />
                                </div>
                                <div class="item">
                                    <input type="email" :aria-label="localizer.get('App.Form.CertifiedEmail')" id="CertifiedEmail" v-model.lazy="formData.certifiedEmail" :class="[{ 'input-validation-error': formHelper.isControlValidationError('CertifiedEmail', validationErrors) }, 'input w300']" />
                                </div>
                            </div>
                        </template>
                        <div class="group" data-type="phone">
                            <div class="item">
                                <label for="PhonePrefix">{{ localizer.get("App.Form.Phone") }}</label>
                                <input type="text" disabled :aria-label="localizer.get('App.Form.PhonePrefix')" id="PhonePrefix" class="input w55" value="+39" />
                            </div>
                            <div class="item">
                                <input type="text" :aria-label="localizer.get('App.Form.PhoneNumber')" id="PhoneNumber" v-model.lazy="formData.phoneNumber" :class="[{ 'input-validation-error': formHelper.isControlValidationError('PhoneNumber', validationErrors) }, 'input w240']" />
                            </div>
                        </div>
                    </fieldset>
                    <fieldset>
                        <div v-if="validationErrors.length !== 0" class="validation-summary-errors">
                            <ul>
                                <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                            </ul>
                        </div>
                        <div class="cta">
                            <button :class="[{ 'loading': isSuccess }, 'button']" @click="submitForm()"><template v-if="!isSuccess">{{ localizer.get("Account.Common.Button_Next") }}</template></button>
                        </div>
                    </fieldset>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount } from "vue";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import Localizer from "@Scripts/Common/Localizer";

const props = defineProps<{ returnUrl: string }>();

const localizer = Localizer;
const formHelper = FormHelper;
const routes = (window as any).App.routes;

const isLoading = ref(false);
const isSuccess = ref(false);
const provinces = ref<any[]>([]);
const profileTaxTypes = ref<any[]>([]);
const countries = ref<any[]>([]);
const activityTypes = ref<any[]>([]);
const editData = ref<any>(null);
const formData = ref<any>({ province: null, profileTaxType: null, country: null, activityType: null, profileSegment: null });
const validationErrors = ref<any[]>([]);
const isCompany = ref(false);
const isItalyCountry = ref(false);
const profileSegments = ref<any[]>([]);

onBeforeMount(async () => {
    await getData();
});

async function getData() {
    editData.value = (await Axios.post(routes.Account_Profile_GetEditData, null, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
    formData.value.name = editData.value.name;
    formData.value.surname = editData.value.surname;
    formData.value.address = editData.value.address;
    formData.value.city = editData.value.city;
    formData.value.postCode = editData.value.postCode;
    formData.value.province = editData.value.province == "" ? null : editData.value.province;
    formData.value.country = editData.value.country == "" ? null : editData.value.country;
    formData.value.profileTaxType = editData.value.profileTaxType == "" ? "Persona fisica" : editData.value.profileTaxType;
    formData.value.taxCode = editData.value.taxCode;
    formData.value.ivaCode = editData.value.ivaCode;
    formData.value.sdiCode = editData.value.sdiCode;
    formData.value.certifiedEmail = editData.value.certifiedEmail;
    formData.value.activityType = editData.value.activityType == "" ? null : editData.value.activityType;
    formData.value.phoneNumber = editData.value.phoneNumber;
    formData.value.profileSegment = editData.value.profileSegment == "" ? null : editData.value.profileSegment;
    provinces.value = editData.value.provinces;
    profileTaxTypes.value = editData.value.profileTaxTypes;
    countries.value = editData.value.countries;
    activityTypes.value = editData.value.activityTypes;
    profileSegments.value = editData.value.profileSegments;
    onProfileTaxTypeChanged();
    onCountryChanged();
}

function onProfileTaxTypeChanged() {
    isCompany.value = formData.value.profileTaxType === "Persona giuridica";
    if (isCompany.value) {
        formData.value.surname = null;
    } else {
        formData.value.ivaCode = null;
        formData.value.activityType = null;
        formData.value.sdiCode = null;
        formData.value.certifiedEmail = null;
    }
}

function onCountryChanged() {
    if (formData.value.country === "IT") {
        isItalyCountry.value = true;
    } else {
        isItalyCountry.value = false;
        formData.value.province = null;
    }
}

async function submitForm() {
    FormHelper.removeFocus();

    validationErrors.value = [];
    isLoading.value = true;

    try {
        let result = (await Axios.post(routes.Account_Profile_Edit, formData.value, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
        isSuccess.value = true;
        window.location.href = result.redirectUrl ? result.redirectUrl : (props.returnUrl ? props.returnUrl : "/");
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }

    isLoading.value = false;
}

function goBack() {
    props.returnUrl ? window.location.href = props.returnUrl : history.back();
}
</script>
